import React from "react";
import { SectionHeader, DataRow } from "./helperComponents";
import { LGrid } from "@loffa/gpp-modules";
import styles from "../Confirmation.module.css";
import { getFileWriteBehaviorName } from "../../OutputDestinations/utils/getFileWriteBehaviorName";
import { SectionProps, OutputDestinationsSectionProps, NotificationsSectionProps,} from "../types/confirmationTypes";
import { GridCellProps, OutputDestinationColumn} from "../../OutputDestinations/types/outputDestinationsTypes";
import { GridCellProps as NotificationGridCellProps, NotificationColumn } from "../../Notifications/types/notificationTypes";

export const GeneralInfoSection = ({ formData }: SectionProps) => {
  const { generalInformation } = formData;
  return (
    <div className={styles.confirmationSection}>
      <SectionHeader title="General Information" />
      <div className={styles.confirmationSectionContent}>
        <DataRow label="Description" value={generalInformation.description} />
        <DataRow label="Industry Identifier" value={generalInformation.industryIdentifier}/>
        <DataRow label="Client" value={generalInformation.client?.formattedName}/>
        <DataRow label="Executing User" value={generalInformation.clientProductUser?.getFullName}/>
        <DataRow label="Product Type" value={generalInformation.productType?.name}/>
        <DataRow label="Auto Import Type" value={generalInformation.autoImportType?.name}/>
        <DataRow label="Template Map" value={generalInformation.templateMap?.name}/>
      </div>
    </div>
  );
};

export const SourceFileSection = ({ formData }: SectionProps) => {
  const { sourceFile } = formData;

  return (
    <div className={styles.confirmationSection}>
      <SectionHeader title="Source File" />
      <div className={styles.confirmationSectionContent}>
        <DataRow
          label="Connection"
          value={
            sourceFile.connection ? (
              <div className={styles.connectionSummary}>
                <span>{sourceFile.connection.description}</span>
                <span className={styles.serverInfo}>
                  Server: {sourceFile.connection.server}
                  {sourceFile.connection.port && ` | Port: ${sourceFile.connection.port}`}
                </span>
              </div>
            ) : null
          }
        />
        <DataRow label="Additional Path" value={sourceFile.additionalPath} />
        <DataRow
          label="File Selector Mode"
          value={
            sourceFile.fileSelectorMode
              ? sourceFile.fileSelectorMode.value === 1
                ? "File Name"
                : sourceFile.fileSelectorMode.value === 2
                ? "Regular Expression"
                : sourceFile.fileSelectorMode.value === 3
                ? "Wild Card"
                : "Unknown"
              : null
          }
        />
        <DataRow label="File Selector" value={sourceFile.fileSelector} />
        <DataRow label="Delete Source File After Import" value={sourceFile.deleteSourceFile ? "Yes" : "No"}/>
      </div>
    </div>
  );
};

export const ValidationsSection = ({ formData }: SectionProps) => {
  const { validations } = formData;
  return (
    <div className={styles.confirmationSection}>
      <SectionHeader title="Validations" />
      <div className={styles.confirmationSectionContent}>
        <DataRow label="Validate Repeated File Name" value={validations.validateRepeatedFileName ? "Yes" : "No"}/>
        <DataRow label="Validate Repeated Checksum" value={validations.validateRepeatedChecksum ? "Yes" : "No"}/>
      </div>
    </div>
  );
};

export const PgpDecryptionSection = ({ formData }: SectionProps) => {
  const { pgpDecryption } = formData;

  return (
    <div className={styles.confirmationSection}>
      <SectionHeader title="PGP Decryption" />
      <div className={styles.confirmationSectionContent}>
        <DataRow label="Use PGP Decryption" value={pgpDecryption.usePgpDecryption ? "Yes" : "No"}/>
        {pgpDecryption.usePgpDecryption && (
          <>
            <DataRow label="Pass Phrase" value={pgpDecryption.pgpPassPhrase} />
            <DataRow label="Private Key" value={pgpDecryption.pgpPrivateKey} />
          </>
        )}
      </div>
    </div>
  );
};

export const OutputDestinationsSection = ({outputDestinations}: OutputDestinationsSectionProps) => {
  const outputDestinationColumns: OutputDestinationColumn[] = [
    {
      field: "type",
      title: "Output Type",
      width: 125,
      cell: (props: GridCellProps) => (<td>{props.dataItem.outputType?.label}</td>),
    },
    {
      field: "connection",
      title: "Connection",
      width: 250,
      cell: (props: GridCellProps) => (<td>{props.dataItem.connection?.description}</td>),
    },
    {
      field: "path",
      title: "Path",
      width: 225,
      cell: (props: GridCellProps) => (
        <td>
          {props.dataItem.connection?.rootPath}
          {props.dataItem.additionalPath && (
            <span className={styles.additionalPath}>
              {props.dataItem.additionalPath}
            </span>
          )}
        </td>
      ),
    },
    {
      field: "fileWriteBehavior",
      title: "File Write Behavior",
      width: 150,
      cell: (props: GridCellProps) => (<td>{getFileWriteBehaviorName(props.dataItem.fileWriteBehavior?.value)}</td>
      ),
    },
  ];

  return (
    <div className={styles.confirmationSection}>
      <SectionHeader title="Output Destinations" />
      <div className={styles.confirmationSectionContent}>
        {outputDestinations && outputDestinations.length > 0 ? (
          <LGrid
            data={outputDestinations}
            columns={outputDestinationColumns}
            sortable
            style={{ height: "250px" }}
            className={styles.confirmationGrid}
          />
        ) : (
          <div className={styles.emptyStateMessage}>
            No output destinations configured
          </div>
        )}
      </div>
    </div>
  );
};

export const NotificationsSection = ({ notifications}: NotificationsSectionProps) => {
  const notificationColumns: NotificationColumn[] = [
    {
      field: "notificationType",
      title: "Notification Type",
      width: 150,
      cell: (props: NotificationGridCellProps) => (<td>{props.dataItem.notificationType.label}</td>),
    },
    {
      field: "to",
      title: "Recipients",
      width: 250,
      cell: (props: NotificationGridCellProps) => (<td>{props.dataItem.to || "-"}</td>),
    },
    {
      field: "cc",
      title: "CC",
      width: 175,
      cell: (props: NotificationGridCellProps) => (<td>{props.dataItem.cc || "-"}</td>
      ),
    },
    {
      field: "bcc",
      title: "BCC",
      width: 175,
      cell: (props: NotificationGridCellProps) => (<td>{props.dataItem.bcc || "-"}</td>),
    },
  ];

  return (
    <div className={styles.confirmationSection}>
      <SectionHeader title="Notifications" />
      <div className={styles.confirmationSectionContent}>
        {notifications && notifications.length > 0 ? (
          <LGrid
            data={notifications}
            columns={notificationColumns}
            sortable
            style={{ height: "250px" }}
            className={styles.confirmationGrid}
          />
        ) : (
          <div className={styles.emptyStateMessage}>
            No notifications configured
          </div>
        )}
      </div>
    </div>
  );
};
